/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : usePageData
 *   Feature      : 226-universal-page-affordances
 *   Description  : The STANDARD adapter every data-backed page routes its page
 *                  header through. Wraps a react-query result into the bundle
 *                  PageLayout/PageHeader consume — soft refresh (refetch), an
 *                  isRefreshing flag, the last-fetch timestamp, an optional
 *                  DB-collection timestamp, and the last error (drives the
 *                  non-blocking error badge). Never reloads the page.
 *   Constitution : v1.23.0
 *   Author       : Peter Dobler
 *   Created      : 2026-07-07
 * ============================================================================
 */
import { useCallback } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

export interface PageDataBundle<T> {
  data: T | undefined;
  isLoading: boolean;
  /** Soft re-fetch — react-query refetch (no page reload). Rejects when the re-fetch fails. */
  refresh: () => Promise<void>;
  isRefreshing: boolean;
  /** When the client last completed a fetch (ms). */
  lastFetchedAt: number;
  /** DB collection time (ms), best-effort — resolved by `getCollectedAt` from the payload. */
  collectedAt?: number;
  error: unknown;
}

export interface UsePageDataOptions<T> {
  /**
   * Extract the DB-collection timestamp from the payload when present
   * (e.g. r => r.lastCollectedAt). Best-effort — omit if the page has none.
   */
  getCollectedAt?: (data: T) => Date | number | string | null | undefined;
}

function toMs(v: Date | number | string | null | undefined): number | undefined {
  if (v == null) return undefined;
  const t = v instanceof Date ? v.getTime() : new Date(v).getTime();
  return Number.isFinite(t) ? t : undefined;
}

/**
 * Adapt a react-query result into the standard page-affordance bundle.
 * Multi-query pages can pass a synthetic result or compose several via a small
 * wrapper; the single-query path covers the vast majority.
 */
export function usePageData<T>(
  query: UseQueryResult<T>,
  options: UsePageDataOptions<T> = {}
): PageDataBundle<T> {
  const { getCollectedAt } = options;

  // throwOnError: a failed re-fetch rejects, so useAutoRefresh can back off (FR-011).
  // Without it refetch() resolves with the error in its result and the backoff never ran.
  // The data react-query already holds stays in `data`; the error drives the badge.
  const { refetch } = query;
  const refresh = useCallback(async () => {
    await refetch({ throwOnError: true });
  }, [refetch]);

  const collectedAt =
    query.data !== undefined && getCollectedAt ? toMs(getCollectedAt(query.data)) : undefined;

  return {
    data: query.data,
    isLoading: query.isLoading,
    refresh,
    isRefreshing: query.isFetching,
    lastFetchedAt: query.dataUpdatedAt,
    collectedAt,
    error: query.error,
  };
}

export default usePageData;
